import { create } from 'zustand'
import type { Character } from '@/entities/character/types/character'
import { EMPTY_CHARACTER } from '@/entities/character/types/character'
import { migrateCharacterKeys } from '../lib/migrate'

// All possible steps; active steps per character are computed in useWizardSteps.
export const WIZARD_STEPS = [
  'Basic Info',
  'Species',
  'Class',
  'Class Features',
  'Background',
  'Feats',
  'Ability Scores',
  'Cantrips',
  'Spells',
  'Equipment',
  'Character Sheet',
] as const

export type WizardStep = (typeof WIZARD_STEPS)[number]

interface CharacterStore {
  character: Character
  step: number
  /**
   * Whether the current step should surface its validation errors. Set true when
   * the user clicks Next before the step is complete; reset to false on every
   * navigation so a freshly entered step starts clean.
   */
  showErrors: boolean
  /**
   * True when the wizard is editing an already-built character (loaded from a
   * saved entry / file, or re-opened from the finished sheet) rather than
   * creating a fresh one. Lets the wizard offer a "Finish" shortcut back to the
   * sheet from any step. Cleared by `reset` (starting a new character).
   */
  editing: boolean
  /**
   * The browser-only draft this new character autosaves to (see `useDraftsStore`), once it
   * has one. Null while editing a built character or before anything was entered.
   */
  draftId: string | null
  /** Continue with a draft: its character, at a step index already resolved from its step key. */
  resumeDraft: (draftId: string, character: Character, step: number) => void
  setDraftId: (draftId: string | null) => void
  update: (partial: Partial<Character>) => void
  next: () => void
  back: () => void
  goTo: (step: number) => void
  setShowErrors: (show: boolean) => void
  setEditing: (editing: boolean) => void
  /** Replace the whole character (e.g. from an uploaded file) and jump to the final sheet. */
  loadCharacter: (character: Character) => void
  reset: () => void
}

export const useCharacterStore = create<CharacterStore>((set) => ({
  character: { ...EMPTY_CHARACTER },
  step: 0,
  showErrors: false,
  editing: false,
  draftId: null,

  resumeDraft: (draftId, character, step) =>
    set({ character: { ...EMPTY_CHARACTER, ...migrateCharacterKeys(character) }, step, showErrors: false, editing: false, draftId }),

  setDraftId: (draftId) => set({ draftId }),

  update: (partial) =>
    set((state) => ({ character: { ...state.character, ...partial } })),

  next: () =>
    set((state) => ({ step: Math.min(state.step + 1, WIZARD_STEPS.length - 1), showErrors: false })),

  back: () =>
    set((state) => ({ step: Math.max(state.step - 1, 0), showErrors: false })),

  goTo: (step) =>
    set({ step: Math.max(0, Math.min(step, WIZARD_STEPS.length - 1)), showErrors: false }),

  setShowErrors: (show) => set({ showErrors: show }),

  setEditing: (editing) => set({ editing }),

  // Step is clamped high; `useWizardSteps` re-clamps to the last *active* step (the sheet).
  // Loading an existing character counts as editing it. Uploaded files may have
  // been downloaded by a pre-species-rename build; normalize their legacy keys.
  loadCharacter: (character) =>
    set({ character: { ...EMPTY_CHARACTER, ...migrateCharacterKeys(character) }, step: WIZARD_STEPS.length - 1, showErrors: false, editing: true, draftId: null }),

  reset: () =>
    set({ character: { ...EMPTY_CHARACTER }, step: 0, showErrors: false, editing: false, draftId: null }),
}))
